<script setup lang="ts">
/**
 * Поле задачи на первом экране.
 *
 * Единственный интерактивный компонент публичного сайта. До него первый экран
 * показывал картинку поля — статичный текст с мигающим курсором и ссылку на
 * регистрацию, — то есть обещал работу, но начать её было негде. Теперь между
 * «придумал» и «начал» нет формы: человек пишет и нажимает, а всё остальное
 * происходит по дороге.
 *
 * Текст уезжает на сервер СРАЗУ, до регистрации, и в браузере остаётся только
 * короткий токен (`handoff/draft.ts`). Так задача переживает то, чего не
 * переживает браузерная память: письмо, открытое в другом браузере, переход с
 * телефона на компьютер, закрытую вкладку.
 *
 * Три вещи, которые здесь легко сломать:
 *
 * 1. Поле не читает localStorage вовсе — ни в setup, ни после. Витрина
 *    гидратируется поверх пререндера (`site/main.ts`, createSSRApp), и первый
 *    клиентский рендер обязан совпасть с серверным; подстановка сохранённого
 *    текста дала бы рассинхрон гидратации. Вернувшемуся на главную нужна не
 *    его старая задача, а вход — подстановка живёт в кабинете, в StartBlock.
 * 2. Стили — только классы lp-*, ни одной утилиты Tailwind: у витрины свой
 *    визуальный язык и своя палитра, и она не пересекается с продуктовой.
 * 3. Поле закрыто от вебвизора классами ym-hide-content и ym-disable-keys.
 *    Метрика на витрине пишет сессии, а сюда человек вставляет бриф своего
 *    клиента — в кабинете запись запрещена ровно по этой причине.
 */
import { computed, ref } from 'vue';
import { readAttribution } from '../../analytics/attribution.js';
import { reachGoal } from '../../analytics/metrika.js';
import { saveDraftToken } from '../../handoff/draft.js';
import { DraftError, postDraft } from '../../site/draft-api.js';
import { SHOWCASE_KINDS, STARTERS } from '../../site/starters.js';

const props = defineProps<{
  /** Жанр, выбранный по умолчанию. На кластерных страницах — их собственный. */
  kind?: string;
  /**
   * Шаблон, с которого начать, — на странице шаблона в каталоге.
   *
   * Человек увидел там конкретный макет и нажал начать: собрать ему что-то
   * другое значило бы обмануть страницу, на которой он это нажал.
   */
  templateSlug?: string;
}>();

/** Тот же потолок, что у поля в кабинете. Держится строкой, а не импортом
 * контрактов: витрина их не тянет. Сервер проверяет своим. */
const MAX_CHARS = 20_000;
/** С какого остатка показывать счётчик. */
const COUNTER_FROM = 500;

const idea = ref('');
const kind = ref(props.kind ?? SHOWCASE_KINDS[0]!.id);
const trap = ref('');
const sending = ref(false);
const error = ref('');

const placeholder = computed(
  () => SHOWCASE_KINDS.find((item) => item.id === kind.value)?.placeholder ?? 'Опишите задачу',
);
const left = computed(() => MAX_CHARS - idea.value.length);
const canSend = computed(() => idea.value.trim().length > 0 && !sending.value);

function pick(id: string): void {
  kind.value = id;
  error.value = '';
}

/**
 * Подставить готовую задачу.
 *
 * Заготовки показываются все и не фильтруются по выбранному жанру: их
 * смысл не «примеры для этого чипа», а «вот с чего можно начать». Нажатие
 * переставляет и жанр — иначе КП собиралось бы по заготовке лендинга.
 */
function useStarter(id: string): void {
  const starter = STARTERS.find((item) => item.id === id);
  if (!starter) return;

  idea.value = starter.text;
  kind.value = starter.kind;
  error.value = '';
}

async function send(): Promise<void> {
  const text = idea.value.trim();
  if (!text || sending.value) return;

  sending.value = true;
  error.value = '';

  try {
    const accepted = await postDraft({
      kind: kind.value,
      idea: text,
      ...(props.templateSlug ? { templateSlug: props.templateSlug } : {}),
      trap: trap.value,
      attribution: readAttribution(),
    });

    saveDraftToken({ token: accepted.token, kind: kind.value });
    reachGoal('prompt_started');

    /*
     * На вход, а не на регистрацию.
     *
     * Вход теперь короче регистрации: почта, код из письма — и всё, аккаунт
     * заводится сам, организация тоже, а документ начинает собираться, пока
     * человек ещё читает экран. Форма регистрации с паролем и подтверждением
     * адреса осталась для тех, кто пришёл по приглашению.
     *
     * Полная перезагрузка, а не переход роутером: кабинет — другая точка входа
     * и другой бандл, маршрута с таким именем в роутере витрины нет вовсе, и
     * RouterLink молча увёл бы на несуществующий адрес.
     */
    window.location.assign('/login');
  } catch (cause) {
    error.value =
      cause instanceof DraftError
        ? cause.message
        : 'Не получилось отправить задачу. Попробуйте ещё раз.';
    sending.value = false;
  }
}
</script>

<template>
  <div class="lp-task">
    <div class="lp-task__label">ЗАДАЧА</div>

    <!-- Вебвизор Метрики пишет сессию целиком, а сюда вставляют бриф клиента.
         Оба класса нужны: первый прячет содержимое, второй — нажатия клавиш. -->
    <textarea
      v-model="idea"
      class="lp-task__input ym-hide-content"
      data-ym-disable-keys
      :placeholder="placeholder"
      :maxlength="MAX_CHARS"
      :disabled="sending"
      rows="3"
      aria-label="Опишите задачу"
      @keydown.ctrl.enter="send"
      @keydown.meta.enter="send"
    />

    <p v-if="left <= COUNTER_FROM" class="lp-task__left">осталось {{ left }}</p>

    <!-- Ловушка. Спрятана от глаз и от чтения с экрана, но заполняется
         роботом, который проходит по всем полям формы подряд. -->
    <input
      v-model="trap"
      class="lp-trap"
      type="text"
      name="website"
      tabindex="-1"
      autocomplete="off"
      aria-hidden="true"
    />

    <div class="lp-task__row">
      <button
        v-for="item in SHOWCASE_KINDS"
        :key="item.id"
        type="button"
        class="lp-chip"
        :class="{ 'lp-chip--on': item.id === kind }"
        :aria-pressed="item.id === kind"
        @click="pick(item.id)"
      >
        {{ item.label }}
      </button>

      <div class="lp-spacer" />

      <button
        type="button"
        class="lp-btn lp-btn--primary"
        :disabled="!canSend"
        @click="send"
      >
        {{ sending ? 'Отправляем…' : 'Собрать черновик' }}
      </button>
    </div>

    <p v-if="error" class="lp-task__error" role="alert">{{ error }}</p>

    <div class="lp-starters">
      <span class="lp-starters__label">Начать с готовой:</span>
      <button
        v-for="starter in STARTERS"
        :key="starter.id"
        type="button"
        class="lp-starter"
        @click="useStarter(starter.id)"
      >
        {{ starter.label }}
      </button>
    </div>
  </div>
</template>
